import type en from '@/locales/en.json';
import { getPromotionStatus, PromotionStatus } from '@/utilities/api/PromotionUtilities';
import { MetricsEndpoint } from '@api/endpoints';
import { TFunction } from 'i18next';
import { countBy } from 'lodash';

type Metrics = MetricsEndpoint.GetPointsMetrics.PointsMetrics;
type Row = MetricsEndpoint.GetPointsMetrics.PointsMetricsRow;

export type ColumnKey = keyof typeof en['pointsMetrics']['columnTooltips'];

/** The metric columns, in display order, shared by the tables and their CSV exports. */
export const metricColumns: { column: ColumnKey, key: keyof Metrics }[] = [
  { column: 'issued', key: 'pointsIssued' },
  { column: 'redeemed', key: 'pointsRedeemed' },
  { column: 'redemptionRate', key: 'pointsRedemptionRate' },
  { column: 'available', key: 'pointsAvailable' },
  { column: 'locked', key: 'pointsLocked' },
  { column: 'reserved', key: 'pointsReserved' },
  { column: 'outstanding', key: 'pointsOutstanding' },
  { column: 'unredeemed', key: 'pointsUnredeemed' },
  { column: 'expired', key: 'pointsExpired' },
  { column: 'expiring30', key: 'pointsExpiring30' },
  { column: 'expiring60', key: 'pointsExpiring60' },
  { column: 'expiring90', key: 'pointsExpiring90' },
  { column: 'customersWithPoints', key: 'customersWithPoints' },
  { column: 'engaged', key: 'customersEngaged' },
  { column: 'notEngaged', key: 'customersNotEngaged' },
];

export const promotionLabel = (promotion: Row['promotions'][number], t: TFunction): string => {
  if (!promotion.promotionId) {
    return t($ => $.pointsMetrics.promotions.programPoints);
  }
  const name = promotion.promotionName ?? t($ => $.pointsMetrics.promotions.unknown);
  return promotion.manufacturerName
    ? t($ => $.pointsMetrics.promotions.manufacturerSponsored, {
      manufacturerName: promotion.manufacturerName,
      name,
    })
    : t($ => $.pointsMetrics.promotions.retailerSponsored, { name });
};

export const countPromotionStatuses = (
  periods: Row['wallets'][number]['promotionPeriods'],
): Record<PromotionStatus, number> => {
  const counts = countBy(periods, getPromotionStatus);
  return {
    [PromotionStatus.EXPIRED]: counts[PromotionStatus.EXPIRED] ?? 0,
    [PromotionStatus.FUTURE]: counts[PromotionStatus.FUTURE] ?? 0,
    [PromotionStatus.RUNNING]: counts[PromotionStatus.RUNNING] ?? 0,
  };
};
